import React, { useState } from "react";
import styled from "@emotion/styled/macro";
import {
  Checkbox,
  Modal,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { useModalInteractionDelay } from "checkout/hooks/useModalInteractionDelay";

const RESIGN_RISK_ACKNOWLEDGEMENT =
  "I want to risk making the customer re-sign.";

type ResignRiskModalProps = {
  testId: string;
  title: string;
  body: string[];
  acknowledgeTestId: string;
  primaryButtonText: string;
  onPrimaryClick: () => void;
  onRiskResigning: () => void;
  onClose: () => void;
};

/**
 * Destructive modal that makes the user wait, then acknowledge the re-sign
 * risk before proceeding. Render it only while open so each open starts a
 * fresh countdown with the acknowledgement cleared.
 */
const ResignRiskModal: React.FC<ResignRiskModalProps> = ({
  testId,
  title,
  body,
  acknowledgeTestId,
  primaryButtonText,
  onPrimaryClick,
  onRiskResigning,
  onClose,
}) => {
  const [hasAcknowledgedResignRisk, setHasAcknowledgedResignRisk] =
    useState(false);
  const { isInteractionDelayed, secondsRemaining } = useModalInteractionDelay();

  return (
    <Modal
      testId={testId}
      type="destructive"
      title={title}
      onClose={onClose}
      disableDismiss={isInteractionDelayed}
      secondaryButton={{
        text: "Proceed & risk re-signing",
        onClick: onRiskResigning,
        disabled: isInteractionDelayed || !hasAcknowledgedResignRisk,
        showSpinnerBesideLabel: isInteractionDelayed,
      }}
      primaryButton={{
        text: primaryButtonText,
        onClick: onPrimaryClick,
        color: "secondary",
        disabled: isInteractionDelayed,
        showSpinnerBesideLabel: isInteractionDelayed,
      }}
      belowButtonsContent={
        isInteractionDelayed ? (
          <BelowButtonText role="status" aria-live="polite">
            <Typography
              variant="p"
              color={tokens.TINTS_OFF_WHITE_20}
              size={tokens.FONT_SIZE_1}
            >
              Please read carefully. You can continue in {secondsRemaining}s
            </Typography>
          </BelowButtonText>
        ) : undefined
      }
    >
      <ModalContent>
        <BodyParagraphs>
          {body.map((paragraph) => (
            <Typography
              key={paragraph}
              variant="p"
              color={tokens.TINTS_OFF_WHITE_20}
              size={tokens.FONT_SIZE_3}
            >
              {paragraph}
            </Typography>
          ))}
        </BodyParagraphs>
        <Checkbox
          checked={hasAcknowledgedResignRisk}
          onChange={(e) => setHasAcknowledgedResignRisk(e.target.checked)}
          label={RESIGN_RISK_ACKNOWLEDGEMENT}
          data-testid={acknowledgeTestId}
          disabled={isInteractionDelayed}
        />
      </ModalContent>
    </Modal>
  );
};

const BelowButtonText = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BodyParagraphs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export { ResignRiskModal };
export type { ResignRiskModalProps };
